'use client'
import { motion } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { SliceComponentProps, SliceLike } from '@/lib/rich-text'

type Platform = {
  name: string
  /** One line under the name: what the platform is, or how Signital runs on it. */
  note: string
  image: string
  image_width: number
  image_height: number
  href: string
}

/**
 * Supported hardware, one tile per platform, each linking to its section of the
 * players page. Sits between the differentiator and the multi-location section
 * on the homepage so the hardware story is visible before the feature list.
 */
export interface PlatformsSlice extends SliceLike<'platforms'> {
  variation: 'default'
  primary: {
    eyebrow: string
    title: string
    text: string
    platforms: Platform[]
    footnote?: string
    button?: { text: string; href: string }
  }
}

export type PlatformsProps = SliceComponentProps<PlatformsSlice>

const Platforms: FC<PlatformsProps> = ({ slice }) => {
  const { eyebrow, title, text, platforms, footnote, button } = slice.primary
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white py-20 md:py-28"
    >
      <div className="container">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-[640px]">
            <p className="eyebrow">{eyebrow}</p>
            <h2 className="display-title mt-3">{title}</h2>
          </div>
          <p className="max-w-[440px] text-[17px] leading-7 text-[#4b4b4b]">{text}</p>
        </div>

        <ul className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7 [&>li:last-child:nth-child(odd)]:col-span-2 sm:[&>li:last-child:nth-child(odd)]:col-span-1">
          {platforms.map((p, i) => (
            <motion.li
              key={p.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: i * 0.05, ease: 'easeOut' }}
            >
              <Link
                href={p.href}
                className="group flex h-full flex-col rounded-2xl bg-[#f6f3f8] p-5 ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_16px_40px_-20px_rgba(129,12,168,0.45)]"
              >
                <div className="flex h-10 items-center">
                  <Image
                    src={p.image}
                    alt=""
                    width={p.image_width}
                    height={p.image_height}
                    className="h-7 w-auto max-w-full object-contain object-left"
                  />
                </div>
                <p className="font-rubik mt-5 text-[16px] font-semibold tracking-tight text-[#2d033b]">
                  {p.name}
                </p>
                <p className="mt-1 text-[13px] leading-5 text-[#4b4b4b]">{p.note}</p>
                <span className="text-primary mt-auto flex items-center gap-1 pt-4 text-[13px] font-semibold opacity-0 transition group-hover:opacity-100">
                  Details
                  <IconComponent icon="ChevronRight" size="xsmall" />
                </span>
              </Link>
            </motion.li>
          ))}
        </ul>

        {(footnote || button) && (
          <div className="mt-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {footnote && (
              <p className="font-rubik text-ink text-[19px] font-medium tracking-tight">{footnote}</p>
            )}
            {button && (
              <Link
                href={button.href}
                className="hover:text-primary inline-flex items-center gap-2 text-[16px] font-semibold text-[#2d033b] transition"
              >
                <IconComponent icon="ChevronRight" size="xsmall" className="text-primary" />
                {button.text}
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default Platforms
